import {
  DeserializePropertyCoordinates,
  NearbyPropertiesQuery,
  PropertyCoordinates,
  PropertyListCountQuery,
  PropertyListQuery,
  PropertyRow,
  PropertyRowModel,
  SerializedPropertyCoordinates,
  ImageUpload,
  ChangeResult,
  UpdateBuilding,
  CreateProperty,
} from 'common';
import { HttpService, HttpServiceResult } from '@/http-services/http.service';
import {
  IPropertyContactInfo,
  Property,
  PropertyModel,
  SubscriptionPricingModel,
  SubscriptionTypeIds,
} from '@ncss/models/dist/v3';
import { AxiosError, CanceledError } from 'axios';

export interface DashboardStats {
  propertyCount: number;
  unitCount: number;
  deviceCount: number;
  onlineGatewayCount: number;
  offlineGatewayCount: number;
}

export type CalculatedSubscriptionPricing = {
  [key in SubscriptionTypeIds]: {
    priceConfig: SubscriptionPricingModel;
    pricingModelCount: number;
    calculatedMonthlyCost: number;
    proratedFirstMonthCost: number;
  };
};

export class PropertyService extends HttpService {
  async create(data: CreateProperty): Promise<HttpServiceResult<Property>> {
    try {
      const p = await this.send<PropertyModel>({
        url: '/Properties',
        data,
        method: 'post',
      });
      return { success: true, data: new Property(p) };
    } catch (e) {
      return {
        success: false,
        error: e instanceof AxiosError && e.response?.data?.error ? e.response.data.error : 'Failed to create property',
      };
    }
  }

  async delete(id: string): Promise<HttpServiceResult> {
    try {
      await this.send({ url: `/Properties/${id}`, method: 'delete' });
      return { success: true, data: { deletedId: id } };
    } catch (e) {
      return {
        success: false,
        error: e instanceof AxiosError && e.response?.data?.error ? e.response.data.error : 'Failed to delete property',
      };
    }
  }

  async updateContactInfo(id: string, contactInfo: IPropertyContactInfo): Promise<HttpServiceResult> {
    try {
      const res = await this.send<PropertyModel>({
        url: `/Properties/${id}/ContactInfo`,
        data: contactInfo,
        method: 'put',
      });
      return { success: true, data: res };
    } catch (e) {
      return {
        success: false,
        error:
          e instanceof AxiosError && e.response?.data?.error ? e.response.data.error : 'Failed to update contact info',
      };
    }
  }

  async setCellularEnabled(propertyId: string, enabled: boolean): Promise<HttpServiceResult> {
    try {
      await this.send({
        url: `/Properties/${propertyId}/Subscriptions/Cellular`,
        method: 'put',
        data: { enabled },
      });
      return { success: true, data: null };
    } catch (e) {
      return {
        success: false,
        error:
          e instanceof AxiosError && e.response?.data?.error
            ? e.response.data.error
            : `Failed to ${enabled ? 'enable' : 'disable'} cellular`,
      };
    }
  }

  findById(id: string) {
    return this.send<PropertyModel>({
      url: `/Properties/${id}`,
      method: 'get',
    }).then((p) => new Property(p));
  }

  async countForUser(filter: PropertyListCountQuery): Promise<number> {
    const response = await this.send<{ count: number }>({
      url: '/UserPropertiesCount',
      method: 'get',
      query: filter,
    });
    return response.count;
  }

  userDashboardStats() {
    return this.send<DashboardStats>({
      url: '/UserDashboardStats',
      method: 'get',
    });
  }

  findAllForUser(filter: PropertyListQuery): Promise<PropertyRow[]> {
    return this.send<PropertyRowModel[]>({
      url: '/UserProperties',
      method: 'get',
      query: filter,
    }).then((docs) => docs.map((d) => new PropertyRow(d)));
  }

  findNearby(filter: NearbyPropertiesQuery): Promise<PropertyRow[]> {
    return this.send<PropertyRowModel[]>({
      url: '/NearbyProperties',
      method: 'get',
      query: filter,
    }).then((docs) => docs.map((d) => new PropertyRow(d)));
  }

  findNearbyCoordinates(filter: NearbyPropertiesQuery, signal?: AbortSignal): Promise<PropertyCoordinates[]> {
    return this.send<SerializedPropertyCoordinates[]>({
      url: '/NearbyPropertyCoordinates',
      method: 'get',
      query: filter,
      signal,
    })
      .then((docs) => docs.map((d) => DeserializePropertyCoordinates(d)))
      .catch((e) => {
        if (!(e instanceof CanceledError)) {
          console.log(e);
        }
        return [];
      });
  }

  recentlyViewedProperties() {
    return this.send<PropertyRowModel[]>({
      url: '/RecentlyViewedProperties',
      method: 'get',
    }).then((docs) => docs.map((d) => new PropertyRow(d)));
  }

  viewedProperty(propertyId: string) {
    return this.send<PropertyRowModel[]>({
      url: '/ViewedProperty/' + propertyId,
      method: 'post',
    }).then((docs) => docs.map((d) => new PropertyRow(d)));
  }

  async addComment(propertyId: string, text: string) {
    await this.send({
      url: `/Properties/${propertyId}/Comments`,
      method: 'post',
      data: { text },
    });
  }

  async removeComment(propertyId: string, commentId: string) {
    await this.send({
      url: `/Properties/${propertyId}/Comments/${commentId}`,
      method: 'delete',
    });
  }

  async addGatewayComment(propertyId: string, text: string) {
    await this.send({
      url: `/Properties/${propertyId}/GatewayComments`,
      method: 'post',
      data: { text },
    });
  }

  async removeGatewayComment(propertyId: string, commentId: string) {
    await this.send({
      url: `/Properties/${propertyId}/GatewayComments/${commentId}`,
      method: 'delete',
    });
  }

  async addGateway(propertyId: string, gatewayId: number): Promise<HttpServiceResult> {
    try {
      await this.send({
        url: `/Properties/${propertyId}/Gateway`,
        method: 'post',
        data: { deviceId: gatewayId },
      });
      return { success: true, data: gatewayId };
    } catch (e) {
      return {
        success: false,
        error: e instanceof AxiosError && e.response?.data?.error ? e.response.data.error : 'Failed to add Gateway',
      };
    }
  }

  async removeGateway(propertyId: string): Promise<HttpServiceResult> {
    try {
      await this.send({
        url: `/Properties/${propertyId}/Gateway`,
        method: 'delete',
      });
      return { success: true, data: propertyId };
    } catch (e) {
      return {
        success: false,
        error: e instanceof AxiosError && e.response?.data?.error ? e.response.data.error : 'Failed to remove Gateway',
      };
    }
  }

  async saveGatewayLocation(propertyId: string, location: { lat: number; long: number }): Promise<HttpServiceResult> {
    try {
      await this.send({
        url: `/Properties/${propertyId}/Gateway/Location`,
        method: 'put',
        data: [location.lat, location.long],
      });
      return { success: true, data: propertyId };
    } catch (e) {
      return {
        success: false,
        error:
          e instanceof AxiosError && e.response?.data?.error
            ? e.response.data.error
            : 'Failed to save Gateway location',
      };
    }
  }

  async addRepeater(propertyId: string, repeaterId: number): Promise<HttpServiceResult> {
    try {
      await this.send({
        url: `/Properties/${propertyId}/Repeaters`,
        method: 'post',
        data: { deviceId: repeaterId },
      });
      return { success: true, data: repeaterId };
    } catch (e) {
      return {
        success: false,
        error: e instanceof AxiosError && e.response?.data?.error ? e.response.data.error : 'Failed to add repeater',
      };
    }
  }

  async removeRepeater(propertyId: string, repeaterId: number): Promise<HttpServiceResult> {
    try {
      await this.send({
        url: `/Properties/${propertyId}/Repeaters/${repeaterId}`,
        method: 'delete',
      });
      return { success: true, data: repeaterId };
    } catch (e) {
      return {
        success: false,
        error: e instanceof AxiosError && e.response?.data?.error ? e.response.data.error : 'Failed to remove repeater',
      };
    }
  }

  async saveRepeaterLocation(
    propertyId: string,
    repeaterId: number,
    location: { lat: number; long: number }
  ): Promise<HttpServiceResult> {
    try {
      await this.send({
        url: `/Properties/${propertyId}/Repeaters/${repeaterId}/Location`,
        method: 'post',
        data: [location.lat, location.long],
      });
      return { success: true, data: repeaterId };
    } catch (e) {
      return {
        success: false,
        error:
          e instanceof AxiosError && e.response?.data?.error
            ? e.response.data.error
            : 'Failed to save repeater location',
      };
    }
  }

  async addRepeaterComment(propertyId: string, repeaterId: number, text: string) {
    await this.send({
      url: `/Properties/${propertyId}/Repeaters/${repeaterId}/Comments`,
      method: 'post',
      data: { text },
    });
  }

  async removeRepeaterComment(propertyId: string, repeaterId: number, commentId: string) {
    await this.send({
      url: `/Properties/${propertyId}/Repeaters/${repeaterId}/Comments/${commentId}`,
      method: 'delete',
    });
  }

  async addPhoto(image: ImageUpload) {
    await this.send({
      url: `/Properties/${image.context.propertyId}/Photos`,
      method: 'post',
      data: image,
    });
  }

  async addGatewayPhoto(image: ImageUpload) {
    await this.send({
      url: `/Properties/${image.context.propertyId}/GatewayPhotos`,
      method: 'post',
      data: image,
    });
  }

  async addRepeaterPhoto(repeaterId: number, image: ImageUpload) {
    await this.send({
      url: `/Properties/${image.context.propertyId}/Repeaters/${repeaterId}/Photos`,
      method: 'post',
      data: image,
    });
  }

  async removeDocument(propertyId: string, name: string) {
    await this.send({
      url: `/Properties/${propertyId}/Documents/${name}`,
      method: 'delete',
    });
  }

  async addDocument(doc: ImageUpload) {
    await this.send({
      url: `/Properties/${doc.context.propertyId}/Documents`,
      method: 'post',
      data: doc,
    });
  }

  updateGatewayDescription(propertyId: string, description: string): Promise<ChangeResult> {
    return this.send({
      url: `/Properties/${propertyId}/GatewayDescription`,
      method: 'put',
      data: { description },
    });
  }

  updateRepeaterDescription(propertyId: string, repeaterId: number, description: string): Promise<ChangeResult> {
    return this.send({
      url: `/Properties/${propertyId}/Repeaters/${repeaterId}/Description`,
      method: 'put',
      data: { description },
    });
  }
  getSubscriptionPricing(propertyId: string) {
    return this.send<CalculatedSubscriptionPricing>({
      url: `/Properties/${propertyId}/SubscriptionPricing`,
      method: 'get',
    });
  }

  async updateBuilding(
    propertyId: string,
    buildingName: string,
    update: UpdateBuilding
  ): Promise<HttpServiceResult<ChangeResult>> {
    try {
      const data = await this.send<ChangeResult>({
        url: `/Properties/${propertyId}/Buildings/${buildingName}`,
        method: 'put',
        data: update,
      });
      return { success: true, data };
    } catch (e) {
      return {
        success: false,
        error:
          e instanceof AxiosError && e.response?.data
            ? e.response.data.error || 'Failed to update building'
            : 'Failed to update building',
      };
    }
  }

  async deleteBuilding(propertyId: string, buildingName: string): Promise<HttpServiceResult> {
    try {
      await this.send<ChangeResult>({
        url: `/Properties/${propertyId}/Buildings/${buildingName}`,
        method: 'delete',
      });
      return { success: true, data: true };
    } catch (e) {
      return {
        success: false,
        error:
          e instanceof AxiosError && e.response?.data
            ? e.response.data.error || 'Failed to delete building'
            : 'Failed to delete building',
      };
    }
  }

  async addBuilding(propertyId: string, name: string): Promise<HttpServiceResult<ChangeResult>> {
    try {
      const data = await this.send<ChangeResult>({
        url: `/Properties/${propertyId}/Buildings`,
        method: 'post',
        data: { name },
      });
      return { success: true, data };
    } catch (e) {
      return {
        success: false,
        error:
          e instanceof AxiosError && e.response?.data
            ? e.response.data.error || 'Failed to add building'
            : 'Failed to add building',
      };
    }
  }
}
